import React from 'react';
import { Swords, Scale, Shield, Crown, Zap, Star, Flame } from 'lucide-react';
import { cn, getInitials, resolveMediaUrl } from '@/lib/utils';
import { getTraitStyle } from '@/lib/traitMeta';
import { RatingIcons } from './PlayerTraits';

// The card's colors follow the form (1-5)...
const FORM_THEMES = [
  { upTo: 1.5, label: 'Gelido', from: '#0a1830', to: '#2f6fb3', accent: '#9fd3ff', ink: '#eaf5ff' },
  { upTo: 2.5, label: 'Freddo', from: '#1b3550', to: '#6c9ec4', accent: '#d2ebff', ink: '#f2f9ff' },
  { upTo: 3.5, label: 'Normale', from: '#33383f', to: '#a7adb6', accent: '#eef0f3', ink: '#ffffff' },
  { upTo: 4.5, label: 'In forma', from: '#5e3300', to: '#e3a21f', accent: '#fff0b3', ink: '#fffaf0' },
  { upTo: 5, label: 'In fiamme', from: '#470505', to: '#ec4b26', accent: '#ffd27a', ink: '#fff6ea' },
];

// ...and a special edition, earned on the pitch, replaces them.
export const EDITIONS = {
  mvp: {
    label: 'MVP', how: "MVP dell'ultima partita", Icon: Crown,
    from: '#050505', to: '#3b3b3b', accent: '#f5c542', ink: '#fbe7a1',
  },
  streak: {
    label: 'Striscia', how: '3 vittorie di fila (o più)', Icon: Zap,
    from: '#022c22', to: '#12a87b', accent: '#a7f3d0', ink: '#ecfdf5',
  },
};

export const formTheme = (form) => {
  const n = Number(form) || 3;
  return FORM_THEMES.find((t) => n <= t.upTo) || FORM_THEMES[2];
};

// form_breakdown.recent is newest first: {outcome: win|draw|loss, mvp_won}.
export const winStreak = (attributes) => {
  const recent = attributes?.form_breakdown?.recent || [];
  const lost = recent.findIndex((r) => r.outcome !== 'win');
  return lost === -1 ? recent.length : lost;
};

export const cardEdition = (attributes) => {
  if (attributes?.form_breakdown?.recent?.[0]?.mvp_won) return 'mvp';
  if (winStreak(attributes) >= 3) return 'streak';
  return null;
};

const ROLES = {
  offensive: { short: 'ATT', label: 'Offensivo', Icon: Swords },
  balanced: { short: 'EQU', label: 'Equilibrato', Icon: Scale },
  defensive: { short: 'DIF', label: 'Difensivo', Icon: Shield },
};
export const roleTag = (playStyle) => ROLES[playStyle] || null;

const FOOT = { right: 'DX', left: 'SX', both: 'DX/SX' };
const num = (v) => (v == null ? '–' : Number(v).toFixed(1));
const shortName = (player) => player?.nickname || (player?.name || '').split(' ')[0] || 'Giocatore';

// Spiky Ultimate Team silhouette with curved edges: corner spikes and a peak
// on top, small spikes on the sides, wings and a point at the bottom. Drawn in
// a 0..1 box and stretched over the card, so it scales with any width.
const OUTLINE = 'M0 0Q.17 .085 .3 .04Q.43 .08 .5 0Q.57 .08 .7 .04Q.83 .085 1 0'
  + 'Q.94 .15 .99 .32Q.94 .6 1 .84Q.88 .855 .74 .925Q.61 .935 .5 1'
  + 'Q.39 .935 .26 .925Q.12 .855 0 .84Q.06 .6 .01 .32Q.06 .15 0 0Z';
const SHAPE = `url("data:image/svg+xml,${encodeURIComponent(
  `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1 1" preserveAspectRatio="none"><path d="${OUTLINE}"/></svg>`,
)}")`;
const shaped = { WebkitMaskImage: SHAPE, maskImage: SHAPE, WebkitMaskSize: '100% 100%', maskSize: '100% 100%' };
// No box around the photo: it fades out on every side.
const VIGNETTE = 'radial-gradient(closest-side, #000 62%, transparent)';

const look = (attributes) => {
  const key = cardEdition(attributes);
  const edition = key ? EDITIONS[key] : null;
  return { key, edition, theme: edition || formTheme(attributes?.form) };
};

const Photo = ({ player, className }) => (player?.picture ? (
  <img
    src={resolveMediaUrl(player.picture)}
    alt=""
    className={cn('object-cover', className)}
    style={{ WebkitMaskImage: VIGNETTE, maskImage: VIGNETTE }}
  />
) : (
  <div className={cn('flex items-center justify-center bg-black/15 font-black', className)} style={{ WebkitMaskImage: VIGNETTE, maskImage: VIGNETTE }}>
    {getInitials(player?.name)}
  </div>
));

const Frame = ({ theme, inset, children }) => (
  <>
    <div className="absolute inset-0" style={{ ...shaped, background: theme.accent }} />
    <div
      className="absolute"
      style={{
        inset,
        ...shaped,
        background: `radial-gradient(120% 70% at 70% 15%, ${theme.to} 0%, ${theme.from} 78%)`,
        color: theme.ink,
      }}
    >
      {children}
    </div>
  </>
);

/**
 * Player card in the Ultimate Team style: skill, attitude and foot on the left,
 * a big photo, the name, skill (stars) / form (flames) / strong / weak foot,
 * the traits and the LeagueHub logo in the point.
 * Colors follow the form; MVP and winning streaks get their own edition.
 */
export const FifaCard = ({ player, attributes = {}, catalog = [], onClick, className }) => {
  const { key, edition, theme } = look(attributes);
  const role = roleTag(attributes.play_style);
  const streak = key === 'streak' ? winStreak(attributes) : 0;
  const stats = [
    ['ABI', Star, attributes.skill_level, 'Abilità', 'card-skill', 'PF', attributes.strong_foot, 'Piede forte'],
    ['FORMA', Flame, attributes.form, `Forma: ${formTheme(attributes.form).label}`, 'card-form', 'PD', attributes.weak_foot, 'Piede debole'],
  ];
  const Tag = onClick ? 'button' : 'div';
  return (
    <Tag
      type={onClick ? 'button' : undefined}
      onClick={onClick}
      aria-label={onClick ? `Scheda di ${player?.name || 'giocatore'}` : undefined}
      data-testid="fifa-card"
      data-edition={key || formTheme(attributes.form).label}
      className={cn(
        'relative block aspect-[5/7] w-full select-none text-left [container-type:inline-size]',
        onClick && 'transition-transform hover:-translate-y-0.5 active:scale-[0.98]',
        className,
      )}
      style={{ filter: 'drop-shadow(0 6px 12px rgba(0,0,0,.3))' }}
    >
      <Frame theme={theme} inset="1.4cqw">
        <div className="absolute left-[8cqw] top-[12cqw] z-10 flex w-[22cqw] flex-col items-center leading-none">
          <span className="text-[15cqw] font-black tracking-tighter" title="Abilità">{num(attributes.skill_level)}</span>
          {role && <span className="mt-[1cqw] text-[6cqw] font-extrabold" title={role.label}>{role.short}</span>}
          {FOOT[attributes.preferred_foot] && (
            <span className="mt-[2cqw] text-[length:max(7px,4.4cqw)] font-bold opacity-80" title="Piede preferito">
              {FOOT[attributes.preferred_foot]}
            </span>
          )}
          {edition && <edition.Icon className="mt-[3cqw] h-[7cqw] w-[7cqw]" style={{ color: theme.accent }} strokeWidth={2.4} />}
        </div>

        <Photo player={player} className="absolute right-[5cqw] top-[6cqw] h-[62cqw] w-[62cqw] text-[18cqw]" />
        {edition && (
          <span
            className="absolute right-[9cqw] top-[11cqw] rounded-full px-[2.4cqw] py-[0.6cqw] text-[length:max(7px,3.6cqw)] font-black uppercase"
            style={{ background: theme.accent, color: theme.from }}
          >
            {edition.label}{streak ? ` ×${streak}` : ''}
          </span>
        )}

        <div className="absolute inset-x-[8cqw] top-[65cqw] text-center">
          <p className="truncate text-[7.2cqw] font-black uppercase leading-tight tracking-tight">{player?.name || 'Giocatore'}</p>
          <p className="truncate text-[length:max(7.5px,4.3cqw)] italic leading-tight" style={{ color: theme.accent }}>
            {player?.nickname ? `«${player.nickname}»` : ' '}
          </p>
          <div className="mx-auto my-[2cqw] h-px w-[70%]" style={{ background: theme.accent, opacity: 0.6 }} />
          <div className="mx-auto w-[88%] space-y-[1.2cqw]">
            {stats.map(([label, Icon, level, title, testId, footLabel, foot, footTitle]) => (
              <div key={label} className="flex items-center justify-between">
                <span className="flex items-center gap-[1.6cqw]">
                  <span className="w-[14cqw] text-left text-[length:max(7px,4cqw)] font-semibold opacity-80">{label}</span>
                  <span style={{ color: theme.accent }}>
                    <RatingIcons
                      Icon={Icon} level={level} max={5} title={title} testId={testId} className="gap-[0.6cqw]"
                      iconClass="h-[5cqw] w-[5cqw]" fullClass="fill-current" emptyClass="fill-transparent opacity-40"
                    />
                  </span>
                </span>
                <span className="flex items-baseline gap-[1.6cqw]" title={footTitle}>
                  <span className="text-[length:max(7px,4cqw)] font-semibold opacity-80">{footLabel}</span>
                  <span className="text-[length:max(9px,5.4cqw)] font-black tabular-nums">{num(foot)}</span>
                </span>
              </div>
            ))}
          </div>
          <div className="mt-[2cqw] flex justify-center gap-[2.6cqw]">
            {(attributes.traits || []).slice(0, 3).map((trait) => {
              const entry = catalog.find((c) => c.key === trait);
              const { Icon } = getTraitStyle(entry || { key: trait });
              return Icon ? (
                <span key={trait} title={entry?.label || trait}>
                  <Icon className="h-[5.6cqw] w-[5.6cqw]" style={{ color: theme.accent }} strokeWidth={2.2} />
                </span>
              ) : null;
            })}
          </div>
        </div>

        <div className="absolute inset-x-0 top-[118cqw] flex flex-col items-center leading-none">
          <img src="/logo.png" alt="" className="h-[6.5cqw] w-auto" />
          <span className="mt-[0.8cqw] text-[length:max(7px,3.4cqw)] font-black tracking-tight">LeagueHub</span>
        </div>
      </Frame>
    </Tag>
  );
};

/**
 * Small card for the pitch and the match lists: skill, attitude, photo, name.
 */
export const FifaMiniCard = ({ player, attributes = {}, onClick, className, note }) => {
  const { key, edition, theme } = look(attributes);
  const role = roleTag(attributes.play_style);
  const Tag = onClick ? 'button' : 'div';
  return (
    <Tag
      type={onClick ? 'button' : undefined}
      onClick={onClick}
      title={player?.name}
      data-testid="fifa-mini-card"
      data-edition={key || formTheme(attributes.form).label}
      className={cn(
        'relative block aspect-[5/7] w-full select-none [container-type:inline-size]',
        onClick && 'transition-transform hover:-translate-y-0.5 active:scale-95',
        className,
      )}
      style={{ filter: 'drop-shadow(0 3px 6px rgba(0,0,0,.35))' }}
    >
      <Frame theme={theme} inset="2cqw">
        <div className="absolute left-[8cqw] top-[9cqw] z-10 flex flex-col items-center leading-none">
          <span className="text-[length:max(10px,17cqw)] font-black tracking-tighter">{num(attributes.skill_level ?? 3)}</span>
          {role && <span className="text-[length:max(7px,10cqw)] font-extrabold">{role.short}</span>}
          {edition && <edition.Icon className="mt-[3cqw] h-[11cqw] w-[11cqw]" style={{ color: theme.accent }} strokeWidth={2.6} />}
        </div>
        <Photo player={player} className="absolute right-[5cqw] top-[7cqw] h-[56cqw] w-[56cqw] text-[16cqw]" />
        <p className="absolute inset-x-[6cqw] top-[64cqw] truncate text-center text-[length:max(8px,12cqw)] font-black uppercase">
          {shortName(player)}
        </p>
        {note && (
          <p className="absolute inset-x-[10cqw] top-[79cqw] truncate text-center text-[length:max(7px,8cqw)] font-semibold opacity-80">{note}</p>
        )}
        <img src="/logo.png" alt="" className="absolute left-1/2 top-[112cqw] h-[12cqw] w-auto -translate-x-1/2" />
      </Frame>
    </Tag>
  );
};

const swatch = (t) => ({ background: `linear-gradient(135deg, ${t.to}, ${t.from})`, boxShadow: `inset 0 0 0 1px ${t.accent}aa` });

// What the colors and the special editions mean.
export const CardLegend = () => (
  <details className="min-w-0 text-sm">
    <summary className="cursor-pointer text-muted-foreground hover:text-foreground">Come cambiano le card</summary>
    <div className="mt-2 space-y-2">
      <p className="text-xs text-muted-foreground">Il colore segue la forma:</p>
      <div className="flex flex-wrap gap-x-3 gap-y-1.5">
        {FORM_THEMES.map((t) => (
          <span key={t.label} className="inline-flex items-center gap-1.5 text-xs">
            <span className="h-3.5 w-3.5 rounded-full" style={swatch(t)} />
            {t.label}
          </span>
        ))}
      </div>
      <p className="text-xs text-muted-foreground">Edizioni speciali, si guadagnano in campo:</p>
      <div className="space-y-1">
        {Object.values(EDITIONS).map((e) => (
          <span key={e.label} className="flex items-center gap-2 text-xs">
            <span className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 font-black" style={{ background: e.accent, color: e.from }}>
              <e.Icon className="h-3 w-3" strokeWidth={2.6} />
              {e.label}
            </span>
            {e.how}
          </span>
        ))}
      </div>
      <p className="text-xs text-muted-foreground">ABI abilità · FOR forma · PF piede forte · PD piede debole · ATT/EQU/DIF attitudine.</p>
    </div>
  </details>
);

export default FifaCard;
